import { useQuery } from "@tanstack/react-query";
import { httpService } from "../../services/httpService";
import { useAuth } from "../helper/useAuth";
import dayjs from "dayjs";
import { API_CONFIG } from "../../shared/constants";
import { IAppSummaryData } from "../../features/Orders/interface";

interface IAppSummaryParams {
  startDate?: Date;
  endDate?: Date;
  page?: number;
  per_page?: number;
  search?: string;
  social_account_id?: number;
  country?: string;
  status?: string;
  app_slug?: string | number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export const useAppOrdersSummary = ({
  startDate,
  endDate,
  page = 1,
  per_page = 15,
  search,
  social_account_id,
  country,
  status,
  app_slug,
  sort_by,
  sort_order,
}: IAppSummaryParams) => {
  const { selectedIntegration } = useAuth();

  const fromDate = startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined;
  const toDate = endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined;

  return useQuery({
    queryKey: [
      "app-orders-summary",
      selectedIntegration?.value,
      fromDate,
      toDate,
      page,
      per_page,
      search,
      social_account_id,
      country,
      status,
      app_slug,
      sort_by,
      sort_order,
    ],
    queryFn: async () => {
      const params: Record<string, any> = {
        page,
        per_page,
      };

      if (selectedIntegration?.value) params.tracking_id = selectedIntegration.value;
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;
      if (search) params.search = search;
      if (social_account_id && !Number.isNaN(social_account_id)) params.social_account_id = social_account_id;
      if (country) params.country = country;
      if (status) params.status = status;
      if (app_slug) params.app_slug = app_slug;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      const response = await httpService.get<IAppSummaryData>(
        API_CONFIG.path.googleIapAppOrdersConsolidated,
        params
      );

      const mappedApps = (response.apps || response.results || []).map((app: any) => ({
        ...app,
        total_orders: app.metrics?.total_orders || 0,
        total_revenue_usd: app.metrics?.total_revenue?.usd || 0,
        total_revenue_inr: app.metrics?.total_revenue?.inr || 0,
        total_tax_usd: app.metrics?.total_tax?.usd || 0,
      }));

      return {
        ...response,
        results: mappedApps,
      };
    },
    enabled: !!selectedIntegration?.value,
    staleTime: 5 * 60 * 1000,
  });
};
